import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Timer, Zap, Trophy, RotateCcw, Crown, Loader2, Link2, Filter } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "@/lib/site";
import { getLeaderboard, submitScore } from "@/lib/api";

const DURATION = 10;

const rankFor = (n) => {
  if (n >= 120) return { t: "KING OF PUMP", c: "#ffb800" };
  if (n >= 90) return { t: "CERTIFIED PUMPER", c: "#39ff14" };
  if (n >= 60) return { t: "GETTING SPICY", c: "#00f0ff" };
  if (n >= 30) return { t: "WARMING UP", c: "#ff007f" };
  return { t: "CHICKEN FEED", c: "#a1a1aa" };
};

const LinkedBadge = ({ linked, i }) => {
  if (!linked) return null;
  return (
    <span
      className="inline-flex items-center text-[#00f0ff]"
      title="Wallet linked (cosmetic — not balance-verified)"
      data-testid={`linked-badge-${i}`}
    >
      <Link2 className="w-3.5 h-3.5" />
    </span>
  );
};

export const MiniGame = () => {
  const [phase, setPhase] = useState("idle");
  const [count, setCount] = useState(0);
  const [time, setTime] = useState(DURATION);
  const [best, setBest] = useState(0);
  const [pops, setPops] = useState([]);
  const [board, setBoard] = useState([]);
  const [name, setName] = useState("");
  const [wallet, setWallet] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showLinkedOnly, setShowLinkedOnly] = useState(false);
  const timerRef = useRef(null);

  const loadBoard = async () => {
    try {
      const data = await getLeaderboard();
      setBoard(data);
    } catch (e) {
      /* silent */
    }
  };

  useEffect(() => {
    setBest(Number(localStorage.getItem("sc_pump_best") || 0));
    setName(localStorage.getItem("sc_pump_name") || "");
    setWallet(localStorage.getItem("sc_pump_wallet") || "");
    loadBoard();
  }, []);

  const spawnPop = () => {
    const id = Math.random().toString(36).slice(2);
    const x = (Math.random() - 0.5) * 120;
    setPops((p) => [...p, { id, x }]);
    setTimeout(() => setPops((p) => p.filter((it) => it.id !== id)), 700);
  };

  const tick = () => {
    if (phase === "idle") {
      setSubmitted(false);
      setPhase("playing");
      setCount(1);
      setTime(DURATION);
      spawnPop();
      timerRef.current = setInterval(() => {
        setTime((t) => {
          if (t <= 1) {
            clearInterval(timerRef.current);
            setPhase("done");
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return;
    }
    if (phase === "playing") {
      setCount((c) => c + 1);
      spawnPop();
    }
  };

  useEffect(() => {
    if (phase === "done") {
      setBest((prev) => {
        const nb = Math.max(prev, count);
        localStorage.setItem("sc_pump_best", String(nb));
        return nb;
      });
    }
  }, [phase, count]);

  const reset = () => {
    clearInterval(timerRef.current);
    setPhase("idle");
    setCount(0);
    setTime(DURATION);
  };

  const submit = async () => {
    const clean = (name || "ANON").trim().slice(0, 16) || "ANON";
    const w = wallet.trim();
    setSubmitting(true);
    try {
      const res = await submitScore(clean, count, w || undefined);
      localStorage.setItem("sc_pump_name", clean);
      if (w) localStorage.setItem("sc_pump_wallet", w);
      setSubmitted(true);
      if (res?.linked) {
        toast.success(`Wallet linked! ${clean} joined the flock 🐔`, { description: "Cosmetic badge earned on the leaderboard." });
      } else if (w) {
        toast.success(`${clean} added to the Hall of Pump!`, { description: "That doesn't look like a valid Solana address — no badge added." });
      } else {
        toast.success(`${clean} added to the Hall of Pump!`);
      }
      await loadBoard();
    } catch (e) {
      toast.error("Could not submit score. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  const rank = rankFor(count);

  return (
    <section id="game" className="relative py-24 sm:py-32 px-5 sm:px-6 border-t border-white/10 overflow-hidden" data-testid="game-section">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mb-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#ff007f] mb-4">Just For Fun</div>
          <h2 className="font-street uppercase tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl">
            The Pump Challenge
          </h2>
          <p className="font-mono text-sm text-white/50 mt-5 max-w-lg leading-relaxed">
            Tap the chicken as fast as you can for {DURATION} seconds. Post your score and
            fight for the crown of King of Pump.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Game panel */}
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-8 items-center rounded-2xl border border-white/10 bg-[#101010] p-8 sm:p-10">
            <div className="flex flex-col items-center gap-6">
              <div className="relative">
                <AnimatePresence>
                  {pops.map((p) => (
                    <motion.span
                      key={p.id}
                      initial={{ opacity: 1, y: 0, scale: 0.6 }}
                      animate={{ opacity: 0, y: -70, scale: 1.1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                      style={{ left: `calc(50% + ${p.x}px)` }}
                      className="absolute top-2 -translate-x-1/2 font-street text-lg text-[#39ff14] pointer-events-none select-none z-10"
                    >
                      +1
                    </motion.span>
                  ))}
                </AnimatePresence>
                <motion.button
                  onClick={tick}
                  disabled={phase === "done"}
                  data-testid="game-chicken-btn"
                  whileTap={{ scale: 0.88, rotate: -4 }}
                  className="relative w-40 h-40 sm:w-48 sm:h-48 rounded-full overflow-hidden border-4 border-[#ff007f] glow-magenta focus:outline-none focus:ring-4 focus:ring-[#ff007f]/40 disabled:opacity-60"
                >
                  <img src={IMAGES.mascot} alt="Tap the Screaming Chicken" className="w-full h-full object-cover pointer-events-none" />
                </motion.button>
              </div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">
                {phase === "idle" && "Tap to start"}
                {phase === "playing" && "PUMP! PUMP! PUMP!"}
                {phase === "done" && "Time's up"}
              </div>
            </div>

            <div className="w-full sm:w-64 grid gap-4">
              <div className="rounded-xl border border-white/10 bg-black/40 p-5">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">
                  <Zap className="w-3.5 h-3.5 text-[#39ff14]" /> Pumps
                </div>
                <div className="font-street text-5xl text-white tabular-nums" data-testid="game-count">{count}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-white/10 bg-black/40 p-4">
                  <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1">
                    <Timer className="w-3.5 h-3.5 text-[#00f0ff]" /> Time
                  </div>
                  <div className="font-mono font-bold text-2xl text-[#00f0ff] tabular-nums" data-testid="game-time">{time}s</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/40 p-4">
                  <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1">
                    <Trophy className="w-3.5 h-3.5 text-[#ffb800]" /> Best
                  </div>
                  <div className="font-mono font-bold text-2xl text-[#ffb800] tabular-nums" data-testid="game-best">{best}</div>
                </div>
              </div>

              <AnimatePresence>
                {phase === "done" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl border p-5"
                    style={{ borderColor: rank.c }}
                    data-testid="game-result"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 text-center">Your rank</div>
                    <div className="font-street text-lg text-center mb-4" style={{ color: rank.c }}>{rank.t}</div>

                    {!submitted ? (
                      <div className="grid gap-2">
                        <input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          maxLength={16}
                          placeholder="YOUR TAG"
                          data-testid="game-name-input"
                          className="w-full font-mono text-sm uppercase tracking-wider bg-black/50 border border-white/15 focus:border-[#39ff14] rounded-lg px-3 py-2.5 outline-none text-white placeholder:text-white/30"
                        />
                        <input
                          value={wallet}
                          onChange={(e) => setWallet(e.target.value)}
                          placeholder="SOLANA WALLET (OPTIONAL)"
                          data-testid="game-wallet-input"
                          className="w-full font-mono text-xs bg-black/50 border border-white/15 focus:border-[#00f0ff] rounded-lg px-3 py-2.5 outline-none text-white placeholder:text-white/30"
                        />
                        <p className="font-mono text-[10px] text-white/35 leading-relaxed">
                          Link a Solana wallet for a cosmetic flock badge (not balance-verified).
                        </p>
                        <button
                          onClick={submit}
                          disabled={submitting}
                          data-testid="game-submit-btn"
                          className="w-full inline-flex items-center justify-center gap-2 font-display font-bold text-sm px-5 py-2.5 rounded-full bg-[#39ff14] text-black hover:bg-[#00f0ff] transition-colors disabled:opacity-60"
                        >
                          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                          Post Score
                        </button>
                      </div>
                    ) : (
                      <div className="font-mono text-xs text-center text-[#39ff14] mb-3">On the board!</div>
                    )}

                    <button
                      onClick={reset}
                      data-testid="game-replay-btn"
                      className="mt-2 w-full inline-flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-wider px-5 py-2.5 rounded-full border border-white/15 text-white/70 hover:border-[#ff007f] hover:text-white transition-colors"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Pump Again
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Leaderboard */}
          <div className="lg:col-span-4 rounded-2xl border border-white/10 bg-[#101010] p-6" data-testid="leaderboard">
            <div className="flex items-center justify-between gap-2 mb-5">
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-[#ffb800]" />
                <h3 className="font-display font-bold uppercase tracking-tight text-sm">Hall of Pump</h3>
              </div>
              <button
                onClick={() => setShowLinkedOnly((v) => !v)}
                data-testid="linked-only-toggle"
                aria-pressed={showLinkedOnly}
                className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-full border transition-colors ${
                  showLinkedOnly
                    ? "bg-[#00f0ff] text-black border-[#00f0ff]"
                    : "border-white/15 text-white/50 hover:border-[#00f0ff] hover:text-white"
                }`}
              >
                <Filter className="w-3 h-3" />
                Linked
              </button>
            </div>
            {(() => {
              const rows = (showLinkedOnly ? board.filter((r) => r.linked) : board).slice(0, 10);
              if (rows.length === 0) {
                return (
                  <div className="font-mono text-xs text-white/40 py-6 text-center" data-testid="leaderboard-empty">
                    {showLinkedOnly ? "No linked flock yet — be the first." : "Be the first King of Pump."}
                  </div>
                );
              }
              return (
                <div className="grid gap-1.5" data-testid="leaderboard-list">
                  {rows.map((row, i) => {
                    const medal = ["#ffb800", "#c0c0c0", "#cd7f32"][i];
                    return (
                      <div
                        key={row.id}
                        className="flex items-center gap-3 rounded-lg px-3 py-2 bg-black/40 border border-white/5"
                        data-testid={`leaderboard-row-${i}`}
                      >
                        <span className="font-street text-sm w-6 text-center" style={{ color: medal || "#71717a" }}>
                          {i + 1}
                        </span>
                        <span className="font-mono text-xs uppercase tracking-wider text-white/80 truncate flex-1 flex items-center gap-1.5">
                          {row.name}
                          <LinkedBadge linked={row.linked} i={i} />
                        </span>
                        <span className="font-mono font-bold text-sm text-[#39ff14] tabular-nums">{row.score}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      <div className="absolute -left-24 bottom-0 w-72 h-72 rounded-full bg-[#ff007f]/10 blur-3xl pointer-events-none" />
    </section>
  );
};
